import {
  camelCase, findKey, reduce, startsWith,
  trimEnd, trimStart, upperFirst, pickBy,
  isUndefined, take,
} from 'lodash';
import { getLocation } from '@sda/utils/location';
import { getEventLocation, getSanitizedString, replaceAll } from '@sda/utils/string';
import { SHOW_END_HOUR_INTERVAL } from '@sda/constants';
import { toBCP47Locale } from '../lang';
import { getDateTime } from '../date';
import type { Beacon } from '@sda/interfaces/beacon';
import type { Link, LinkList, UtmMedia } from '@sda/interfaces/link';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type {
  BandcampInput, Embed, Services,
  ServiceUrlInput, Size, StreamingProfileDetails, SoundcloudConfiguration,
  BandcampConfiguration, VimeoConfiguration, SpotifyConfiguration, YoutubeConfiguration, CreateLinkDto,
} from './interfaces';
import type { DateTimeInput } from '../date/interfaces';
import type { PublicShow } from '../../interfaces/show';

/**
 * Generates a site link to the specified path
 * @param {string} path Will be trimmed from any slash (/) from the beginning of str
 * @return {string}
 */
export const baseUrl = (path: string): string => `${process.env.SITE_BASE_URL}/${trimStart(path, '/')}`;

/**
 * Generates a link to the squarespace marketing site
 * @param  {string} path
 * @return {string}
 */
export const newsUrl = (path: string): string => `https://news.sidedooraccess.com/${trimStart(path, '/')}`;

/**
 * Generates an API link to the specified path
 * @param {string} path Will be trimmed from any slash (/) from the beginning of str
 * @return {string}
 */
export const apiUrl = (path: string): string => `${process.env.API_BASE_URL}/${trimStart(path, '/')}`;

/**
 * Generates a short link to the specified path
 * @param {string} path Will be trimmed from any slash (/) from the beginning of str
 * @return {string}
 */
export const shortUrl = (path: string): string => `${process.env.SHORT_BASE_URL}/${trimStart(path, '/')}`;

/**
 * Encodes a URI to be fully RFC3986-compliant
 * https://stackoverflow.com/questions/18251399/why-doesnt-encodeuricomponent-encode-single-quotes-apostrophes
 * @param  {string} str
 * @return {string}
 */
export const rfc3986EncodeURIComponent = (str: string | number): string => encodeURIComponent(str).replace(/[!'()*]/g, escape);

/**
 * Generates a URI with the given base URL and query parameters
 * @param  {string} baseUrl
 * @param  {object} params
 * @return {string}
 */
export const buildURIFromParams = (base: string, params: { [id: string]: any }): string => {
  const finalParams = pickBy(params, (param) => param !== null && !isUndefined(param));

  if (Object.keys(finalParams).length === 0) {
    return base;
  }

  return trimEnd(
    reduce(
      finalParams,
      (url, value, key) => `${url}${rfc3986EncodeURIComponent(key)}=${rfc3986EncodeURIComponent(value)}&`,
      base.indexOf('?') === -1 ? `${trimEnd(base, '?')}?` : base,
    ),
    '&',
  );
};

/**
 * Get act path from a given resource specification - actId or vanityUrl
 * @param  {string} path
 * @return {string}
 */
export const getActPath = (path: string): string => `/artists/${path}`;

/**
 * Get venue path from a given vanityUrl
 * @param  {string} vanityUrl
 * @return {string}
 */
export const getVenuePath = (vanityUrl: string): string => `/hosts/${vanityUrl}`;

/**
 * Get show path from a given showId
 * @param  {string} showId
 * @return {string}
 */
export const getShowPath = (showId: string): string => `/shows/${showId}`;

/**
 * Get show group path from a given showId
 * @param  {string} showGroupId
 * @return {string}
 */
export const getShowGroupPath = (showGroupId: string): string => `/series/${showGroupId}`;

/**
 * Get ticket path from a given showId
 * @param  {string} showId
 * @return {string}
 */
export const getTicketPath = (showId: string): string => `/mytickets/${showId}`;

/**
 * Generate a link to the specified act profile based on given actId or vanityUrl
 * @param  {string} path
 * @return {string}
 */
export const getActUrl = (path: string): string => baseUrl(getActPath(path));

/**
 * Generate a link to the specified venue profile
 * @param  {string} venueId
 * @return {string}
 */
export const getVenueUrl = (venueId: string): string => baseUrl(getVenuePath(venueId));

/**
  * Generate a link to the user profile home (former activityFeed)
  * @return {string}
  */
export const getProfileHomeUrl = (): string => baseUrl('/mytickets');

export const getReviewPath = (showId: string) => `/reviews/${showId}`;

export const getReviewUrl = (showId: string) => baseUrl(getReviewPath(showId));

/**
  * Generate the public-facing URL for this show
  * @param  {string} showId
  * @param  {string} [utmMedium]
  * @return {string}
  */
export const getShowUrl = (showId: string, utmMedium?: string): string => {
  let url = baseUrl(getShowPath(showId));

  if (utmMedium) {
    url = buildURIFromParams(url, {
      utm_source: 'show',
      utm_medium: utmMedium,
      utm_campaign: 'share',
      utm_content: showId,
    });
  }

  return url;
};

/**
  * Generate a show link using the show's LinkList
  * @param  {PublicShow} show
  * @param  {UtmMedia} utmMedium
  * @return {string}
  */
export const getShowLink = (show: PublicShow & FetchDataType, utmMedium: UtmMedia): string => {
  const id = findKey(show.links, (link) => link.utmMedium === utmMedium);

  if (id) {
    return shortUrl(id);
  }

  return getShowUrl(show.id, utmMedium);
};

/**
   * Generate the public-facing door sale URL for this show
   * @param  {string} showId
   * @return {string}
   */
export const getDoorUrl = (show: Pick<FetchDataType, 'id'>): string => baseUrl(`/myshows/${show.id}/audience`);

/**
  * Generate the public-facing URL for this show
  * @param  {string} showId
  * @return {string}
  */
export const getThreadUrl = (show: Pick<PublicShow, 'masterThreadId'>): string => baseUrl(`/messages/${show.masterThreadId}`);

/**
  * Generate the public-facing URL for this series
  * @param  {string} showGroupId
  * @param  {boolean} [includeUtm]
  * @return {string}
  */
export const getShowGroupUrl = (showGroupId: string, includeUtm = false): string => {
  let url = baseUrl(getShowGroupPath(showGroupId));

  if (includeUtm) {
    // https://gitlab.com/sidedoor/sidedooraccess/issues/707
    url = buildURIFromParams(url, {
      utm_medium: 'event',
      utm_campaign: 'show-events',
      utm_content: showGroupId,
    });
  }

  return url;
};

/**
  * Returns the ticket wallet url
  * @return {string}
  */
export const getTicketWalletUrl = (): string => baseUrl('/mytickets');

/**
 * Generate the private URL for an invitation
 * @param  {string} invitationId
 * @return {string}
 */
export const getInvitationUrl = (invitationId: string): string => baseUrl(`/invite/${invitationId}`);

export const getConnectPath = (showCallId: string): string => `/connect/${showCallId}`;

/**
  * Generate the public-facing URL for a show call
  * @param  {string} showId
  * @param  {string} [utmMedium]
  * @return {string}
  */
export const getConnectUrl = (showCallId: string, utmMedium?: UtmMedia): string => {
  let url = baseUrl(getConnectPath(showCallId));

  if (utmMedium) {
    // https://gitlab.com/sidedoor/sidedooraccess/issues/707
    url = buildURIFromParams(url, {
      utm_source: 'show-call',
      utm_medium: utmMedium,
      utm_campaign: 'share',
      utm_content: showCallId,
    });
  }

  return url;
};

/**
  * Generate a show call link using the show call's LinkList
  * @param  {Beacon} showCall
  * @param  {UtmMedia} utmMedium
  * @return {string}
  */
export const getShowCallLink = (showCall: Beacon & FetchDataType, utmMedium: UtmMedia): string => {
  const id = findKey(showCall.links, (link) => link.utmMedium === utmMedium);

  if (id) {
    return shortUrl(id);
  }

  return getConnectUrl(showCall.id, utmMedium);
};

/**
 * Ensures the specified link starts with https://
 * @param  {string} url
 */
export const getExternalUrl = (url: string): string => (
  startsWith(url, 'http://')
    || startsWith(url, 'https://')
    || startsWith(url, '//')
    || startsWith(url, 'spotify') // ensure spotify URLs pass the test
    ? url : `http://${url}`);

/**
 * Retrieves a query string parameter from the current URL
  * https://css-tricks.com/snippets/javascript/get-url-variables/
 * @param  {string} variable
 * @return {string}
 */
export const getQueryVariable = (variable: string): string => {
  const input = window?.location || baseUrl('/');
  const url = new URL(input as string);

  return url.searchParams.get(variable) || '';
};

export const getServiceUrls = (props: ServiceUrlInput): Services => {
  const {
    address, description, locale, startTime,
    timezone, title, url,
    skipLocalization = false,
  } = props;

  /**
   * Sanitizes input for ICS
   * @param  {string} text
   * @return {string}
   */
  const formatIcsText = (text = ''): string => text
    .replace(/\\/gm, '\\\\')
    .replace(/\r?\n/gm, '\n')
    .replace(/;/gm, '\\;')
    .replace(/,/gm, '\\,');

  const formatTime = (date: DateTimeInput) => {
    let dateTime = getDateTime(date);

    if (!skipLocalization) {
      dateTime = dateTime.setLocale(toBCP47Locale(locale)).setZone(timezone);
    }

    return dateTime.toFormat('yyyyLLdd\'T\'HHmmss').replace('+00:00', 'Z');
  };

  const start = formatTime(startTime);
  const end = formatTime(getDateTime(startTime).plus({ hours: SHOW_END_HOUR_INTERVAL }));

  const icsDescription = skipLocalization ? `
    Reminder: Access will be provided 30 minutes prior to show time.
    To access this show, you must be logged in to your Side Door account.
    This allows us to keep your ticket(s) secure.
  ` : description;

  return {
    google: buildURIFromParams(
      'https://www.google.com/calendar/render',
      {
        action: 'TEMPLATE',
        text: title || '',
        dates: `${start || ''}/${end || ''}`,
        details: description || '',
        location: address,
        sprop: 'name:',
        ctz: skipLocalization ? '' : timezone,
      },
    ),
    ics: `data:text/calendar;charset=utf8,${rfc3986EncodeURIComponent([
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `URL:${url}`,
      `DTSTART:${start || ''}`,
      `DTEND:${end || ''}`,
      `SUMMARY:${formatIcsText(title) || ''}`,
      `DESCRIPTION:${formatIcsText(icsDescription)}`,
      `LOCATION:${address}`,
      'END:VEVENT',
      'END:VCALENDAR'].join('\n'))}`,
  };
};

/**
 * Generates a bandcamp embed URL
 * @param  {string} parts
 * @return {string}
 */
export const buildBandcampUrl = (props: BandcampInput): string => {
  const { album, track } = props;
  const parts = [];

  if (album) {
    parts.push(`album=${album}`);
  }

  if (track) {
    parts.push(`track=${track}`);
  }

  return `https://bandcamp.com/EmbeddedPlayer/${parts.join('/')}`;
};

/**
  * Get the encoded url for a google map search.
  * @param  {GeoPoint} address
  * @return {string}
  */
export const getMapsSearchUrlForAddress = (address: FirebaseFirestore.GeoPoint): string => {
  if (!address) {
    return '';
  }

  const { lat, lng } = getLocation(address);

  return `https://google.com/maps/search/${encodeURIComponent(`${lat},${lng}`)}`;
};

/**
 * Returns a map image from an address. This can then be embedded in the show email or in other locations available
 * only to users who have made a ticket purchase.
 * @param  {GeoPoint} address
 * @return {string}
 */
export const getStaticMapUrlForAddress = (address: FirebaseFirestore.GeoPoint): string => {
  const { lat, lng } = getLocation(address);

  const defaults = {
    zoom: 15,
    size: '640x480',
    markerColor: 'red',
    scale: 2,
  };

  const markers = `color:${defaults.markerColor}|${lat},${lng}`;
  return `https://maps.googleapis.com/maps/api/staticmap?scale=${defaults.scale}&zoom=${defaults.zoom}&size=${defaults.size}&markers=${encodeURIComponent(markers)}&key=${process.env.GOOGLE_MAPS_API_KEY}`;
};

/**
 * Given the existing vanityUrlCache (cacheArray) and the vanityUrlLowercase string, add the url to the beginning
 * of the array; if the array is longer than 5 items, pop the last item out of the array and return the array.
 * @param {string[]} cacheArray - array to use as initial state
 * @param {string} vanityUrlLowercase - new string to add to array
 * @return {string[]} new array
*/
export const addToVanityUrlCacheArray = (cacheArray: string[], vanityUrlLowercase: string): string[] => {
  let array = [vanityUrlLowercase, ...cacheArray || []];

  // set the vanityUrlCache to an array of the last 5
  if (array.length > 5) {
    array = take(array, 5);
  }

  return array;
};

/**
 * Given a string, return a URI encoded, PascalCase string with specific characters replaced with words
 * @param  {string} vanity URL to be modified
 * @param  {boolean} setPascalCase choose to PascalCase the vanityUrl
 * @return {string}
 */
export const getSanitizedVanityUrl = (vanityUrl: string, setPascalCase = true): string => (
  rfc3986EncodeURIComponent(
    setPascalCase
      ? upperFirst(camelCase(getSanitizedString(vanityUrl)))
      : replaceAll(getSanitizedString(vanityUrl), ' ', ''),
  )
);

/**
 * Extract service and id from streaming profile uri.
 * Returns an object with the extracted service and artist ID.
 * Either values may be null if not found.
 *
 * @param  {string} streamingProfileUri
 * @return {StreamingProfileDetails}
 */
export const getStreamingProfileDetails = (streamingProfileUri: string): StreamingProfileDetails => {
  let artistId = null;
  let service = null;

  const externalUri = getExternalUrl(streamingProfileUri);

  // Soundcloud
  const soundcloudUrlRegex = /^https?:\/\/(soundcloud\.com|snd\.sc)\/(.*)$/;
  const soundcloudUrlMatches = soundcloudUrlRegex.exec(externalUri);

  if (soundcloudUrlMatches) {
    [, artistId] = soundcloudUrlMatches;
    service = 'soundcloud';

    return {
      artistId, // returns the domain
      service,
      uri: externalUri,
      oEmbed: true,
    };
  }

  // Spotify
  const spotifyUrlRegex = /https:\/\/.*\.spotify\.com\/(.*)/;
  const spotifyUrlMatches = spotifyUrlRegex.exec(streamingProfileUri);

  if (spotifyUrlMatches) {
    [, artistId] = spotifyUrlMatches;
    service = 'spotify';

    return {
      artistId,
      service,
      uri: externalUri,
      oEmbed: true,
    };
  }

  const spotifyOtherRegex = /spotify:(.*)/;
  const spotifyOtherMatches = spotifyOtherRegex.exec(streamingProfileUri);

  if (spotifyOtherMatches) {
    [, artistId] = spotifyOtherMatches;
    service = 'spotify';

    return {
      artistId,
      service,
      uri: streamingProfileUri,
      oEmbed: true,
    };
  }

  // Youtube
  const youtubeUrlRegex = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;
  const youtubeUrlMatches = youtubeUrlRegex.exec(externalUri);

  if (youtubeUrlMatches) {
    const videoId = youtubeUrlMatches[5];

    artistId = videoId;
    service = 'youtube';

    return {
      artistId, // returns either the subdomain (if present) or domain
      service,
      uri: externalUri,
      oEmbed: true,
    };
  }

  // Vimeo
  const vimeoUrlRegex = /^https?:\/\/vimeo\.com\/(.*)/;
  const vimeoUrlMatches = vimeoUrlRegex.exec(externalUri);

  if (vimeoUrlMatches) {
    [, artistId] = vimeoUrlMatches;
    service = 'vimeo';

    return {
      artistId,
      service,
      uri: externalUri,
      oEmbed: true,
    };
  }

  // Bandcamp
  const bandcampUrlRegex = /^(http|https):\/\/((.*)\.)?bandcamp\.com\/(.*)/;
  const bandcampUrlMatches = bandcampUrlRegex.exec(externalUri);

  const bandcampOtherRegex = /\[bandcamp(.*?)/;
  const bandcampOtherMatches = bandcampOtherRegex.exec(externalUri);

  if (bandcampUrlMatches || bandcampOtherMatches) {
    let bandcampUri = externalUri;
    let bandcampAlbumId = '';
    let bandcampTrackId = '';
    let generateEmbed = true;
    artistId = true; // assumes the bandcamp uri or code provided is valid

    // If the external URI is an iframe we need to extract the SRC attribute
    // Else If the string is a wordpress shortcode
    if (bandcampUri.includes('<iframe')) {
      generateEmbed = false;

      let albumRegex = /album=(\d*)([^]+)/;
      let trackRegex = /track=(\d*)([^]+)/;

      let albumId = albumRegex.exec(bandcampUri);
      if (albumId) {
        [, bandcampAlbumId] = albumId;
      }

      let trackId = trackRegex.exec(bandcampUri);
      if (trackId) {
        [, bandcampTrackId] = trackId;
      }

      bandcampUri = buildBandcampUrl({ album: bandcampAlbumId, track: bandcampTrackId });
    } else if (bandcampUri.includes('[bandcamp')) {
      let albumRegex = /album=(\d*)([^]+)/;
      let trackRegex = /track=(\d*)([^]+)/;
      generateEmbed = false;

      let albumId = albumRegex.exec(bandcampUri);
      if (albumId) {
        [, bandcampAlbumId] = albumId;
      }

      let trackId = trackRegex.exec(bandcampUri);
      if (trackId) {
        [, bandcampTrackId] = trackId;
      }

      bandcampUri = buildBandcampUrl({ album: bandcampAlbumId, track: bandcampTrackId });
    }

    service = 'bandcamp';

    return {
      artistId,
      service,
      uri: bandcampUri,
      oEmbed: false,
      generateBandcampEmbedUrl: generateEmbed,
    };
  }

  return {
    artistId,
    service,
  };
};

export const EMBED_CONFIG = {
  SOUNDCLOUD: {
    URL: 'https://soundcloud.com/oembed?url=',
    KEY: 'soundcloudUri',
    METAKEY: 'soundcloudUriCache',
    PROVIDER_NAME: 'soundcloud',
  } as SoundcloudConfiguration,
  SPOTIFY: {
    URL: 'https://embed.spotify.com/oembed/?url=',
    KEY: 'spotifyUri',
    METAKEY: 'spotifyUriCache',
    PROVIDER_NAME: 'spotify',
  } as SpotifyConfiguration,
  YOUTUBE: {
    URL: 'https://www.youtube.com/oembed?url=',
    KEY: 'youtubeUri',
    METAKEY: 'youtubeUriCache',
    PROVIDER_NAME: 'youtube',
  } as YoutubeConfiguration,
  VIMEO: {
    URL: 'https://vimeo.com/api/oembed.json?url=',
    KEY: 'vimeoUri',
    METAKEY: 'vimeoUriCache',
    PROVIDER_NAME: 'vimeo',
  } as VimeoConfiguration,
  BANDCAMP: {
    URL: '',
    KEY: 'bandcampUri',
    METAKEY: 'bandcampUriCache',
    PROVIDER_NAME: 'bandcamp',
  } as BandcampConfiguration,
};

/**
 * Takes an URI as src, width, height and returns the embed code.
 * @param  {string} src - uri to be embedded
 * @param  {Size} height
 * @param  {Size} width
 * @return {Embed}
 */
export const getBasicEmbed = (src: string, height: Size = '100%', width: Size = '100%'): Embed => ({
  height,
  src,
  width,
  allowtransparency: 'true',
  frameBorder: 0,
});

/**
 * Takes a Spotify uri and returns the embed code.
 * @param  {string} src - the spotify uri to be embedded
 * @param  {Size} height
 * @param  {Size} width
 * @return {Embed}
 */
export const getSpotifyEmbed = (src: string, height: Size = '100%', width: Size = '100%'): Embed => ({
  height,
  src,
  width,
  allow: 'encrypted-media',
  allowtransparency: 'true',
  frameBorder: 0,
});

/**
 * Takes a SoundCloud artist ID and returns the embed code.
 * @param  {string} src - the soundcloud uri
 * @param  {Size} height
 * @param  {Size} width
 * @return {Embed}
 */
export const getSoundCloudEmbed = (src: string, height: Size = 450, width: Size = '100%'): Embed => ({
  height,
  width,
  scrolling: 'no',
  frameBorder: 'no',
  allow: 'autoplay',
  src: buildURIFromParams(
    `${src}&`,
    {
      color: '#ff5500',
      auto_play: false,
      hide_related: false,
      show_comments: true,
      show_user: true,
      show_reposts: false,
      show_teaser: true,
      visual: true,
    },
  ),
});

/**
 * Retrieves the URL hash, if any
 * @param  {string} location - the location to retrieve the hash from
 * @return {string}
 */
export const getUrlHashContents = (location: string): string => {
  const { hash } = new URL(location);

  if (hash) {
    return hash.substring(1);
  }

  return '';
};

/**
 * Generates a ticket QR code url
 * @param {string} ticketId
 * @return {string}
 */
export const ticketQRCodeUrl = (ticketId: string): string => `sda://ticket/${ticketId}`;

export const getShowCardDescription = (show: Pick<PublicShow, 'startTime' | 'city' | 'region' | 'country'>): string => {
  let description = '';

  if (show.city) {
    description += getEventLocation(show);
  }

  if (show.startTime) {
    if (description) {
      description += ', ';
    }

    description += getDateTime(show.startTime).toFormat('LLL d, t');
  }

  return description;
};

export const createLinkObject = (data: CreateLinkDto, getNextId: () => string): LinkList => {
  const links: Link[] = data.utmMedia.map((medium) => {
    const utmMedium = medium;
    const {
      utmCampaign, utmContent, utmSource, target, targetId,
    } = data;

    return {
      target,
      targetId,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
      numImpressions: 0,
    };
  });

  return reduce(links, (results, link) => {
    const id = getNextId();

    return {
      ...results,
      [id]: link,
    };
  }, {});
};
